import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import {
  isMobileBreakpoint,
  fetchConfig,
  ensureImageWidth,
  preloadImage,
  prefersReducedMotion,
} from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {HTMLElement | null} */
  #overlay = null;
  /** @type {HTMLElement | null} */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #backdrop = null;
  /** @type {import('./product-card').ProductCard | null} */
  #productCard = null;
  /** @type {HTMLElement | null} */
  #cardGallery = null;
  /** @type {boolean} */
  #isAddingToCart = false;
  /** @type {HTMLTemplateElement | null} */
  #template = null;
  /** @type {Promise<HTMLTemplateElement | null> | null} */
  #templatePrefetchPromise = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | null} */
  #updateDebounceTimer = null;
  /** @type {string | null} */
  #currentVariantId = null;
  /** @type {number} */
  #savedScrollY = 0;
  /** @type {((e: TouchEvent) => void) | null} */
  #touchMoveHandler = null;

  static #SWIPE_THRESHOLD = 60;
  #swipeStartY = 0;
  #swipeCurrentY = 0;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchStartHandler = null;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchMoveHandler = null;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchEndHandler = null;
  /** @type {((e: Event) => void) | null} */
  #quickAddIntentHandler = null;
  /** @type {HTMLElement | null} Element that opened the size picker; refocused after a successful add. */
  #openerElement = null;

  /** Gets the product page URL. @returns {string} */
  get productPageUrl() {
    if (!this.#productCard) {
      this.#productCard = /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );
    }
    const cardUrl = this.#productCard?.getProductCardLink()?.href;
    if (cardUrl) return cardUrl;

    // On the PDP this component is rendered in the sticky add-to-cart host, outside any
    // product-card, so there is no card link to read. Without this fallback the getter returns ''
    // and fetchQuickAddTemplate throws on `new URL('')`, so the template can never be refreshed.
    const handle = this.dataset.productHandle;
    if (!handle) return '';

    const root = window.Shopify?.routes?.root ?? '/';
    return new URL(`${root}products/${handle}`.replace(/\/{2,}/g, '/'), window.location.origin).href;
  }

  /**
   * Fetches updated quick-add template via Section Rendering API
   * @param {string} [variantId] - Optional variant ID to fetch specific variant context
   * @returns {Promise<HTMLTemplateElement | null>}
   */
  async fetchQuickAddTemplate(variantId) {
    try {
      const productUrl = new URL(this.productPageUrl);
      if (!productUrl) return null;

      // Cancel any in-flight request
      if (this.#abortController) {
        this.#abortController.abort();
      }

      this.#abortController = new AbortController();

      // Build Section Rendering API URL
      const apiUrl = new URL(productUrl);
      apiUrl.searchParams.set('section_id', 'section-rendering-product-card');

      if (variantId) {
        apiUrl.searchParams.set('variant', variantId);
      }

      const response = await fetch(apiUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return null;

      const html = await response.text();

      // Parse the response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract the template
      const template = doc.querySelector('[data-quick-add-template]');

      this.#abortController = null;

      return /** @type {HTMLTemplateElement | null} */ (template);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching quick-add template:', error);
      }
      return null;
    }
  }

  /**
   * @param {HTMLTemplateElement | null} template
   * @returns {boolean}
   */
  #isDeferredTemplate(template) {
    if (!template) return true;
    if (template.hasAttribute('data-quick-add-template-deferred')) return true;
    return template.content.childElementCount === 0;
  }

  /**
   * A colorway switch on the PDP swaps in a different product, and the morph updates this
   * component's attributes and its hidden variant input — but not the quick-add template. morph
   * walks `childNodes`, and a `<template>`'s children live in `.content`, so the rendered size
   * buttons keep the previous product's variant ids and the drawer adds the wrong colour.
   * A hydrated template is therefore only trustworthy if it still covers the selected variant.
   * @param {HTMLTemplateElement | null} template
   * @returns {boolean}
   */
  #isStaleTemplate(template) {
    if (!template) return false;

    const currentVariantId = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    )?.value;
    if (!currentVariantId) return false;

    const renderedIds = Array.from(template.content.querySelectorAll('[data-variant-id]')).map(
      (button) => /** @type {HTMLElement} */ (button).dataset.variantId,
    );
    if (!renderedIds.length) return false;

    return !renderedIds.includes(currentVariantId);
  }

  /**
   * Resolves the quick-add template from DOM or fetches it on demand.
   * @returns {Promise<HTMLTemplateElement | null>}
   */
  async #resolveTemplateForInteraction() {
    const existingTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('[data-quick-add-template]')
    );
    this.#template = existingTemplate;
    if (
      existingTemplate &&
      !this.#isDeferredTemplate(existingTemplate) &&
      !this.#isStaleTemplate(existingTemplate)
    ) {
      return existingTemplate;
    }

    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    const variantId = variantIdInput?.value || this.#currentVariantId || undefined;
    const fetchedTemplate = await this.fetchQuickAddTemplate(variantId);
    if (!fetchedTemplate) return null;

    if (existingTemplate) {
      existingTemplate.replaceWith(fetchedTemplate);
    } else {
      this.appendChild(fetchedTemplate);
    }
    this.#template = fetchedTemplate;
    return fetchedTemplate;
  }

  #handleTemplateIntent = () => {
    if (
      this.#template &&
      !this.#isDeferredTemplate(this.#template) &&
      !this.#isStaleTemplate(this.#template)
    ) {
      return;
    }
    if (this.#templatePrefetchPromise) return;
    this.#templatePrefetchPromise = this.#resolveTemplateForInteraction().finally(() => {
      this.#templatePrefetchPromise = null;
    });
  };

  connectedCallback() {
    super.connectedCallback();

    // Cache product card reference
    this.#productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    // Get initial template reference (will be refreshed before use)
    this.#template = this.querySelector('[data-quick-add-template]');

    // Ensure button click handler is attached
    const button = this.querySelector('.quick-add__button');
    if (button && !button.hasAttribute('data-listener-attached')) {
      button.addEventListener('click', this.handleClick);
      if (!this.#quickAddIntentHandler) {
        this.#quickAddIntentHandler = this.#handleTemplateIntent;
      }
      button.addEventListener('pointerenter', this.#quickAddIntentHandler, { passive: true });
      button.addEventListener('focus', this.#quickAddIntentHandler);
      button.setAttribute('data-listener-attached', 'true');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cancel any pending fetch
    if (this.#abortController) {
      this.#abortController.abort();
    }

    // Clear debounce timer
    if (this.#updateDebounceTimer) {
      clearTimeout(this.#updateDebounceTimer);
    }

    // Remove button listener
    const button = this.querySelector('.quick-add__button');
    if (button) {
      button.removeEventListener('click', this.handleClick);
      if (this.#quickAddIntentHandler) {
        button.removeEventListener('pointerenter', this.#quickAddIntentHandler);
        button.removeEventListener('focus', this.#quickAddIntentHandler);
      }
      button.removeAttribute('data-listener-attached');
    }

    this.#removeOverlay();
    this.#removeDrawer();

    // Clear cache
    this.#template = null;
    this.#productCard = null;
    this.#cardGallery = null;
    this.#currentVariantId = null;
  }

  /** Creates the desktop overlay. */
  #createOverlay() {
    if (this.#overlay) return; // Already created

    this.#cardGallery = this.#productCard?.querySelector('.card-gallery') ?? null;
    if (!this.#cardGallery) return;

    this.#overlay = document.createElement('div');
    this.#overlay.className = 'quick-add__overlay';
    this.#cardGallery.appendChild(this.#overlay);

    // Stop clicks on the overlay from propagating to the product card link
    this.#overlay.addEventListener('click', this.#handleOverlayClick);

    // Add mouse leave handlers to close overlay when hovering out of the image container
    this.#cardGallery.addEventListener('mouseleave', this.#handleCardGalleryMouseLeave);

    // Add keyboard handler for Escape key
    document.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Handles clicks on the overlay to prevent propagation to product card link
   * @param {Event} event
   */
  #handleOverlayClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Handles keydown events for accessibility
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#overlay?.classList.contains('visible')) {
      this.#hideOverlay();
      // Return focus to the quick add button
      const button = this.querySelector('.quick-add__button');
      if (button instanceof HTMLElement) {
        button.focus();
      }
    }
  };

  /** Creates the mobile drawer. */
  #createDrawer() {
    if (this.#drawer) return; // Already created

    this.#backdrop = document.createElement('div');
    this.#backdrop.className = 'quick-add__drawer-backdrop';
    document.body.appendChild(this.#backdrop);

    this.#drawer = document.createElement('div');
    this.#drawer.className = 'quick-add__drawer';
    document.body.appendChild(this.#drawer);

    this.#backdrop.addEventListener('click', this.#closeDrawer);
  }

  /** Handles card gallery mouse leave - closes overlay on desktop when leaving image container. */
  #handleCardGalleryMouseLeave = () => {
    if (!isMobileBreakpoint()) {
      this.#hideOverlay();
    }
  };

  /**
   * Public method called by product-card when variant changes
   * @param {any} variant - The new variant object
   */
  updateForVariant(variant) {
    // Debounce rapid variant changes
    if (this.#updateDebounceTimer) {
      clearTimeout(this.#updateDebounceTimer);
    }

    this.#updateDebounceTimer = setTimeout(async () => {
      const variantId = variant?.id?.toString();
      if (!variantId || variantId === this.#currentVariantId) return;

      this.#currentVariantId = variantId;

      const isOpen =
        this.#overlay?.classList.contains('visible') || this.#drawer?.classList.contains('open');
      const hasHydratedTemplate = this.#template && !this.#isDeferredTemplate(this.#template);
      if (!isOpen && !hasHydratedTemplate) return;

      // Fetch updated template via Section Rendering API
      const newTemplate = await this.fetchQuickAddTemplate(variantId);

      if (newTemplate) {
        if (this.#template) {
          this.#template.replaceWith(newTemplate);
        } else {
          this.appendChild(newTemplate);
        }
        this.#template = newTemplate;

        // Re-render if overlay/drawer is open
        if (this.#overlay?.classList.contains('visible')) {
          this.#renderInOverlay();
        } else if (this.#drawer?.classList.contains('open')) {
          this.#renderInDrawer();
        }
      }
    }, 150); // Debounce for 150ms
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if overlay is already open (desktop) - toggle close
    if (!isMobileBreakpoint() && this.#overlay?.classList.contains('visible')) {
      this.#hideOverlay();
      return;
    }

    this.#rememberOpener(event);
    this.#template = await this.#resolveTemplateForInteraction();
    if (!this.#template) {
      console.warn('Quick-add template not found');
      return;
    }

    if (isMobileBreakpoint()) {
      this.#createDrawer();
      this.#renderInDrawer();
    } else {
      this.#createOverlay();
      this.#renderInOverlay();
    }
  };

  /**
   * Prefers the clicked button because Safari and iOS VoiceOver don't focus buttons on click.
   * Sticky ATC, hotspots and the hybrid PDP call handleClick with an undispatched event (no
   * target), and predictive search closes its dialog first (hiding the clicked button), so those
   * fall back to the focused element.
   * @param {Event} event
   */
  #rememberOpener(event) {
    const clickedButton = event.target instanceof Element ? event.target.closest('button') : null;
    const activeElement = document.activeElement;
    const candidates = [clickedButton, activeElement === document.body ? null : activeElement];
    this.#openerElement =
      /** @type {HTMLElement | undefined} */ (
        candidates.find((el) => el instanceof HTMLElement && el.getClientRects().length > 0)
      ) ?? null;
  }

  #restoreFocusToOpener() {
    const opener = this.#openerElement;
    this.#openerElement = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }

  /** Renders template content in the desktop overlay. */
  #renderInOverlay() {
    if (!this.#overlay || !this.#template) return;

    // Clone the template content
    const content = this.#template.content.cloneNode(true);

    // Clear and append new content
    this.#overlay.innerHTML = '';
    this.#overlay.appendChild(content);

    // Attach event listeners to buttons
    this.#attachButtonListeners(this.#overlay);

    this.#showOverlay();
  }

  /** Renders template content in the mobile drawer. */
  #renderInDrawer() {
    if (!this.#drawer || !this.#template) return;

    const fragment = document.createDocumentFragment();

    // Add close button with handle
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'quick-add__drawer-close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.addEventListener('click', this.#closeDrawer);
    const handle = document.createElement('span');
    handle.className = 'handle';
    closeButton.appendChild(handle);
    fragment.appendChild(closeButton);

    // Clone the template content
    const content = this.#template.content.cloneNode(true);
    fragment.appendChild(content);

    // Clear and append new content
    this.#drawer.innerHTML = '';
    this.#drawer.appendChild(fragment);

    // Attach event listeners to buttons
    this.#attachButtonListeners(this.#drawer);

    this.#openDrawer();
  }

  /**
   * Attaches event listeners to option buttons
   * @param {HTMLElement} container - The container element
   */
  #attachButtonListeners(container) {
    const buttons = container.querySelectorAll('.quick-add__option-button[data-variant-id]');

    buttons.forEach((button) => {
      const variantId = button.getAttribute('data-variant-id');
      const isAvailable = button.getAttribute('data-available') === 'true';

      if (variantId) {
        if (isAvailable) {
          button.addEventListener('click', () =>
            this.addToCart(variantId, /** @type {HTMLElement} */ (button)),
          );
        } else {
          const sizeLabel =
            button.querySelector('.quick-add__option-button-label')?.textContent?.trim() || '';
          const btn = /** @type {HTMLElement} */ (button);
          button.addEventListener('click', () =>
            this.#openNotifyMeDrawer(container, variantId, sizeLabel, {
              productId: btn.dataset.productId || undefined,
              productTitle: btn.dataset.productTitle || undefined,
              productType: btn.dataset.productType || undefined,
              variantSku: btn.dataset.variantSku || undefined,
              variantTitle: btn.dataset.variantTitle || undefined,
              variantPrice: btn.dataset.variantPrice || undefined,
              variantCompareAtPrice: btn.dataset.variantCompareAtPrice || undefined,
            }),
          );
        }
      }
    });
  }

  /**
   * Opens the global notify-me drawer for an unavailable variant
   * @param {HTMLElement} _container - The container element (unused, kept for signature compat)
   * @param {string} variantId
   * @param {string} sizeLabel - The size label to display
   * @param {import('./notify-me-form.js').NotifyMeProductData} [productData] - Product data for tracking
   */
  #openNotifyMeDrawer(_container, variantId, sizeLabel, productData) {
    if (isMobileBreakpoint()) {
      this.#closeDrawer();
    } else {
      this.#hideOverlay();
    }

    setTimeout(() => {
      window.openNotifyMeDrawer?.(variantId, sizeLabel, productData);
    }, 100);
  }

  /**
   * Gets preorder line item metadata rendered by quick-add liquid.
   * @returns {Record<string, { rawDate: string; message: string }> | null}
   */
  #getPreorderMap() {
    const script = this.querySelector('script[data-preorder-map]');
    if (!script) return null;

    try {
      const preorderMap = JSON.parse(script.textContent);
      if (!preorderMap || typeof preorderMap !== 'object' || Array.isArray(preorderMap)) {
        return null;
      }
      return preorderMap;
    } catch {
      return null;
    }
  }

  /**
   * Adds a variant to cart
   * @param {string} variantId
   * @param {HTMLElement} [clickedButton] - The size option button (for variant title and ATC popup productInfo)
   */
  async addToCart(variantId, clickedButton) {
    if (this.#isAddingToCart) return;
    this.#isAddingToCart = true;

    // Disable all size buttons in the container while adding to cart
    const activeContainer = this.#overlay?.classList.contains('visible')
      ? this.#overlay
      : this.#drawer;

    /** @type {NodeListOf<HTMLButtonElement> | never[]} */
    const optionButtons = activeContainer?.querySelectorAll('.quick-add__option-button') ?? [];
    optionButtons.forEach((btn) => {
      btn.disabled = true;
    });

    // Preload product image immediately so it's in the browser cache by the time the popup opens.
    // This runs before the cart fetch, giving the image the full network round-trip to load.
    const earlyImageUrl = ensureImageWidth(
      clickedButton?.dataset?.variantImage ?? this.dataset.productImage,
    );
    if (earlyImageUrl) preloadImage(earlyImageUrl);
    const earlyUpsellImageUrl = ensureImageWidth(this.dataset.upsellImage?.trim(), 450);
    if (earlyUpsellImageUrl) preloadImage(earlyUpsellImageUrl);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Append line item properties: final sale and preorder
    if (this.dataset.finalSale) {
      formData.append('properties[_finalSale]', this.dataset.finalSale);
    }

    const preorderData = this.#getPreorderMap()?.[variantId];
    if (preorderData) {
      formData.append('properties[_preOrderDate]', preorderData.rawDate);
      formData.append('properties[Preorder]', preorderData.message);
    }

    const atcPropertyAncestor = this.closest('[data-atc-property-key]');
    if (atcPropertyAncestor) {
      const key = atcPropertyAncestor.getAttribute('data-atc-property-key');
      const value = atcPropertyAncestor.getAttribute('data-atc-property-value');
      if (key && value !== null) {
        formData.append(`properties[${key}]`, value);
      }
    } else {
      const collectionHandleEl = this.closest('[data-collection-handle]');
      if (collectionHandleEl) {
        const collectionHandle = collectionHandleEl.getAttribute('data-collection-handle');
        if (collectionHandle) {
          formData.append('properties[_PLP_ATC_Path]', `/collections/${collectionHandle}`);
        }
      }
    }

    try {
      const config = fetchConfig('javascript', { body: formData });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && !data.status) {
        const productHandle =
          this.dataset.productHandle ||
          (this.productPageUrl
            ? new URL(this.productPageUrl).pathname.replace(/^\/products\//, '').replace(/\/$/, '')
            : '');
        const variantTitle =
          clickedButton?.querySelector('.quick-add__option-button-label')?.textContent?.trim() ??
          '';
        const sizeLabel = variantTitle;
        const imageUrl = clickedButton?.dataset?.variantImage ?? this.dataset.productImage;
        const productInfo = productHandle
          ? {
              productHandle,
              ...(this.dataset.productTitle && { productTitle: this.dataset.productTitle }),
              ...(variantTitle && { variantTitle }),
              ...(sizeLabel && { sizeLabel }),
              ...(imageUrl && { imageUrl }),
            }
          : undefined;

        const upsellProductId = this.dataset.upsellProductId?.trim();
        const upsellInfo =
          upsellProductId && this.dataset.upsellProductHandle
            ? {
                pairedProductId: upsellProductId,
                pairedProductHandle: this.dataset.upsellProductHandle?.trim() ?? '',
                title: this.dataset.upsellProductTitle?.trim() ?? '',
                imageUrl: this.dataset.upsellImage?.trim() || null,
                upsellTitle: this.dataset.upsellTitle?.trim() ?? '',
                upsellText: this.dataset.upsellText?.trim() ?? '',
                upsellButtonText: this.dataset.upsellButtonText?.trim() ?? '',
              }
            : undefined;

        const atcSource = this.dataset.atcSource?.trim() || 'quick-add';
        // Close drawer/overlay first so the popup opens onto a clean screen with no overlap.
        this.#closeDrawer();
        this.#hideOverlay();
        // Must run before dispatch: the ATC success popup reads document.activeElement as its return-focus target.
        this.#restoreFocusToOpener();

        document.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: atcSource,
            productId: this.dataset.productId,
            itemCount: 1,
            productInfo,
            upsellInfo,
          }),
        );
      } else {
        console.error('Failed to add to cart:', data.message || data.description || data);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.#isAddingToCart = false;
      optionButtons.forEach((btn) => {
        btn.disabled = false;
      });
    }
  }

  /** Shows the overlay. */
  #showOverlay() {
    if (this.#overlay) {
      this.#overlay.classList.add('visible');
      this.setAttribute('data-overlay-open', '');

      // Focus first size option for keyboard accessibility
      requestAnimationFrame(() => {
        const firstButton = this.#overlay?.querySelector('.quick-add__option-button');
        if (firstButton instanceof HTMLElement) {
          firstButton.focus();
        }
      });
    }
  }

  /** Hides and resets the overlay. */
  #hideOverlay() {
    if (this.#overlay) {
      this.#overlay.classList.remove('visible');
      this.removeAttribute('data-overlay-open');
      // Clear content after transition completes
      setTimeout(() => {
        if (this.#overlay && !this.#overlay.classList.contains('visible')) {
          this.#overlay.innerHTML = '';
        }
      }, 300); // Match transition duration
    }
  }

  /** Opens the drawer. */
  #openDrawer() {
    if (this.#drawer && this.#backdrop) {
      this.#savedScrollY = window.scrollY;

      // Save horizontal scroll positions of carousels before overflow:hidden resets them on iOS Safari.
      const carouselScrollPositions = /** @type {Map<Element, number>} */ (new Map());
      document.querySelectorAll('slideshow-slides').forEach((el) => {
        carouselScrollPositions.set(el, /** @type {HTMLElement} */ (el).scrollLeft);
      });

      document.documentElement.classList.add('quick-add-drawer-open');

      // Restore carousel positions after the layout recalculation caused by overflow:hidden.
      if (carouselScrollPositions.size > 0) {
        requestAnimationFrame(() => {
          carouselScrollPositions.forEach((scrollLeft, el) => {
            const htmlEl = /** @type {HTMLElement} */ (el);
            if (htmlEl.scrollLeft !== scrollLeft) htmlEl.scrollLeft = scrollLeft;
          });
        });
      }

      this.#addTouchMoveLock();

      this.#drawer.classList.add('open');
      this.#backdrop.classList.add('visible');
      this.#bindSwipeListeners();

      // Focus first size option for keyboard accessibility
      requestAnimationFrame(() => {
        const firstButton = this.#drawer?.querySelector('.quick-add__option-button');
        if (firstButton instanceof HTMLElement) {
          firstButton.focus();
        }
      });
    }
  }

  /** Closes the drawer. */
  #closeDrawer = () => {
    if (this.#drawer && this.#backdrop) {
      // Save carousel positions before removing overflow:hidden (iOS Safari may reset on layout change).
      const carouselScrollPositions = /** @type {Map<Element, number>} */ (new Map());
      document.querySelectorAll('slideshow-slides').forEach((el) => {
        carouselScrollPositions.set(el, /** @type {HTMLElement} */ (el).scrollLeft);
      });

      this.#unbindSwipeListeners();
      this.#drawer.style.transform = '';
      this.#drawer.style.transition = '';
      this.#drawer.classList.remove('open');
      this.#backdrop.classList.remove('visible');
      document.documentElement.classList.remove('quick-add-drawer-open');
      this.#removeTouchMoveLock();
      if (!document.documentElement.hasAttribute('data-scrolling-to-top')) {
        window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
      }

      if (carouselScrollPositions.size > 0) {
        requestAnimationFrame(() => {
          carouselScrollPositions.forEach((scrollLeft, el) => {
            const htmlEl = /** @type {HTMLElement} */ (el);
            if (htmlEl.scrollLeft !== scrollLeft) htmlEl.scrollLeft = scrollLeft;
          });
        });
      }
    }
  };

  /** Removes the overlay. */
  #removeOverlay() {
    if (this.#overlay) {
      // Remove event listeners
      this.#overlay.removeEventListener('click', this.#handleOverlayClick);
      if (this.#cardGallery) {
        this.#cardGallery.removeEventListener('mouseleave', this.#handleCardGalleryMouseLeave);
      }
      document.removeEventListener('keydown', this.#handleKeyDown);

      this.#overlay.remove();
      this.#overlay = null;
      this.#cardGallery = null;
    }
  }

  /** Removes the drawer. */
  #removeDrawer() {
    const hadDrawer = !!this.#drawer;
    if (this.#drawer) {
      this.#unbindSwipeListeners();
      this.#drawer.remove();
      this.#drawer = null;
    }
    if (this.#backdrop) {
      this.#backdrop.remove();
      this.#backdrop = null;
    }
    document.documentElement.classList.remove('quick-add-drawer-open');
    this.#removeTouchMoveLock();
    // Only restore scroll when the drawer was actually open — not during
    // disconnectedCallback DOM teardown (e.g. grid.innerHTML replacement).
    if (hadDrawer && !document.documentElement.hasAttribute('data-scrolling-to-top')) {
      window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
    }
  }

  /** Whether the current touch started on the drawer handle */
  #swipeOnHandle = false;

  #bindSwipeListeners() {
    if (!this.#drawer) return;
    this.#swipeTouchStartHandler = (e) => {
      const firstTouch = e.touches[0];
      if (!firstTouch) return;
      this.#swipeStartY = firstTouch.clientY;
      this.#swipeCurrentY = this.#swipeStartY;
      this.#swipeOnHandle =
        e.target instanceof Element &&
        !!e.target.closest('.quick-add__drawer') &&
        (e.target.closest('.quick-add__drawer')?.querySelector('.quick-add__options')?.scrollTop ===
          0 ||
          false);
    };
    this.#swipeTouchMoveHandler = (e) => {
      if (!this.#swipeOnHandle) return;
      const firstTouch = e.touches[0];
      if (!firstTouch) return;
      const deltaY = firstTouch.clientY - this.#swipeStartY;
      if (deltaY <= 0) return;
      e.preventDefault();
      this.#swipeCurrentY = firstTouch.clientY;
      const drawer = this.#drawer;
      if (!drawer) return;
      if (!prefersReducedMotion()) {
        drawer.style.transform = `translateY(${deltaY}px)`;
      }
    };
    this.#swipeTouchEndHandler = () => {
      if (!this.#swipeOnHandle) return;
      const drawer = this.#drawer;
      if (!drawer) return;
      const deltaY = this.#swipeCurrentY - this.#swipeStartY;
      if (deltaY >= QuickAddComponent.#SWIPE_THRESHOLD) {
        drawer.style.transform = '';
        this.#closeDrawer();
      } else {
        drawer.style.transition = 'transform 0.2s ease';
        drawer.style.transform = '';
        drawer.addEventListener(
          'transitionend',
          () => {
            if (this.#drawer) this.#drawer.style.transition = '';
          },
          { once: true },
        );
      }
    };
    this.#drawer.addEventListener('touchstart', this.#swipeTouchStartHandler, { passive: true });
    this.#drawer.addEventListener('touchmove', this.#swipeTouchMoveHandler, { passive: false });
    this.#drawer.addEventListener('touchend', this.#swipeTouchEndHandler, { passive: true });
  }

  #unbindSwipeListeners() {
    if (!this.#drawer) return;
    if (this.#swipeTouchStartHandler)
      this.#drawer.removeEventListener('touchstart', this.#swipeTouchStartHandler);
    if (this.#swipeTouchMoveHandler)
      this.#drawer.removeEventListener('touchmove', this.#swipeTouchMoveHandler);
    if (this.#swipeTouchEndHandler)
      this.#drawer.removeEventListener('touchend', this.#swipeTouchEndHandler);
    this.#swipeTouchStartHandler = null;
    this.#swipeTouchMoveHandler = null;
    this.#swipeTouchEndHandler = null;
  }

  /**
   * Prevents background touch-scrolling on iOS Safari while the drawer is open.
   * Allows scrolling within the drawer's options list and within any dialog opened on top
   * (e.g. size guide from window.openSizeGuideForProduct).
   */
  #addTouchMoveLock() {
    if (this.#touchMoveHandler) return;
    this.#touchMoveHandler = (e) => {
      if (
        e.target instanceof Element &&
        (e.target.closest('.quick-add__drawer') || e.target.closest('dialog'))
      )
        return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', this.#touchMoveHandler, { passive: false });
  }

  #removeTouchMoveLock() {
    if (this.#touchMoveHandler) {
      document.removeEventListener('touchmove', this.#touchMoveHandler);
      this.#touchMoveHandler = null;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
